import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Date and time formatting utilities
export function formatDateTime(date: Date | string): string {
  const d = new Date(date)
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Price formatting utility
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price)
}

// Tracking URL utility
export function getTrackingUrl(trackingNumber: string, carrier: string = 'ups'): string {
  const carriers: Record<string, string> = {
    ups: `https://www.ups.com/track?tracknum=${trackingNumber}`,
    fedex: `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`,
    usps: `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`,
    dhl: `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`
  }
  
  return carriers[carrier.toLowerCase()] || carriers.ups
}

// Generate unique ID
export function uniqueID(): string {
  return Math.random().toString(36).substr(2, 9)
}

// Local storage utilities
export function has(key: string): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(key) !== null
}

export function get(key: string): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(key)
}

export function set(key: string, value: string): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(key, value)
}

export function remove(key: string): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(key)
}

// Color options utility
export interface ColorOption {
  id: string
  name: string
  value: string
}

export const colorOptions: ColorOption[] = [
  { id: '1', name: 'Blue', value: '#3b82f6' },
  { id: '2', name: 'Green', value: '#10b981' },
  { id: '3', name: 'Purple', value: '#8b5cf6' },
  { id: '4', name: 'Red', value: '#ef4444' },
  { id: '5', name: 'Yellow', value: '#f59e0b' },
  { id: '6', name: 'Pink', value: '#ec4899' }
]

export function getColorOption(id: string): ColorOption | undefined {
  return colorOptions.find(option => option.id === id)
}

// Ensure extended user utility
export function ensureExtendedUser(user: any): any {
  return {
    ...user,
    following: user.following || [],
    followers: user.followers || [],
    suspended: user.suspended || false,
    bio: user.bio || '',
    bioLink: user.bioLink || null,
    profileURL: user.profileURL || null,
    emailVerified: user.emailVerified || null,
    emailVerificationToken: user.emailVerificationToken || '',
    emailVerificationTokenExpiry: user.emailVerificationTokenExpiry || null,
    passwordResetToken: user.passwordResetToken || '',
    passwordResetTokenExpiry: user.passwordResetTokenExpiry || null,
    updatedAt: user.updatedAt || new Date(),
    hashedPassword: user.hashedPassword || ''
  }
}
